import { ContentService } from '@blk/cwp-ng-framework';
import { AbstractRenderer, Alignment, BoxModelConstraint, CellPanel, ColumnComposition, ColumnCompositions, RowPanel, TextRenderer } from '@blk/simba';
import { ChartType, ColumnChartCustomAttributes, ColumnChartName } from '@ddt-web/chart';
import { CellData, TabData, TableData, TabUtil } from '@ddt-web/data-service';
import { NumberUtil } from '@ddt-web/generic-utils';
import { PortfolioDotRenderer } from '@ddt-web/pdf-utils';
import * as fill from 'lodash/fill';
import * as get from 'lodash/get';
import * as isNil from 'lodash/isNil';

import { HighchartsSVGComponent } from '../../highchart/highchart-svg-component';
import { DefaultColumnComposition } from '../column-compositions/default-column-composition';

export class TableUtils {
	private static leftColumnProportion: number = 50;
	private static leftColumnProportionAsLegend: number = 20;
	private static rightColumnProportion: number = 50;
	private static rightColumnProportionAsLegend: number = 80;

	public static getTableAndChartHeaderRow(
		name: string,
		tabData: TabData,
		columnCompositions: ColumnCompositions,
		chartType: ChartType = ChartType.Bar,
		headerConstraints?: Partial<BoxModelConstraint>,
		isForLegend: boolean = false,
		chartName?: ColumnChartName,
		shouldUseWeightAsFirstCell: boolean = true,
		shouldMatchPortfolioColor: boolean = false,
		indexOfBenchmarkPortfolio?: number,
		leftColumnProportion?: number,
		rightColumnProportion?: number,
		fieldUsedInChartHeader?: string
	): AbstractRenderer<SVGGElement> {
		if (!columnCompositions || !columnCompositions.headers) {
			console.error(`Column configurations are not defined, table: ${name} will not render!`);
			return;
		}
		leftColumnProportion = leftColumnProportion ?? this.leftColumnProportion;
		rightColumnProportion = rightColumnProportion ?? this.rightColumnProportion;

		const tableHeaderCells: AbstractRenderer<SVGElement>[] = TableUtils.getTableHeaderCells(tabData, columnCompositions, isForLegend, shouldUseWeightAsFirstCell);
		let chartHeader: Array<RowPanel> = [];
		let paddingLeft = 0;
		if (chartType === ChartType.Bar) {
			const proportions = TableUtils.calculateColumnProportions(tabData.tableData[0].headersX.length, 100);
			const legendHeader = tabData.tableData[0].headersX.map(
				(header: CellData, index: number) => {
					const colorIndex = shouldMatchPortfolioColor && (index >= indexOfBenchmarkPortfolio) ? index + 1 : index;
					return new ColumnComposition({
						cells: header,
						widthProportion: proportions[index],
						horizontalAlignment: Alignment.Horizontal.Left,
						verticalAlignment: Alignment.Vertical.Bottom,
						renderer: new PortfolioDotRenderer(header, colorIndex, Alignment.Horizontal.Left, 100, ['text-bold', 'text-8'], {}, 9)
					});
				}
			);
			legendHeader.forEach((configs: ColumnComposition) => {
				chartHeader = [...chartHeader, new RowPanel('bodyRow', [configs.renderer.render()], {}, Alignment.Horizontal.Left, Alignment.Vertical.Bottom)];
			});
		} else {
			if (chartType === ChartType.Column) {
				paddingLeft = 23;
			}
			if (chartName === ColumnChartName.NileRisk || chartName === ColumnChartName.NileExposure) {
				paddingLeft = 30; // more space needed to fit yAxis title for pac risk charts
			}

			const headers = !isNil(fieldUsedInChartHeader) ? columnCompositions.headers.filter(header => header?.cells?.[0]?.config?.id === fieldUsedInChartHeader) : columnCompositions.headers.slice(1);

			const chartHeaderCells = headers.map((config: ColumnComposition, index: number, arr: Array<ColumnComposition>) => {
				const cell = config.cells instanceof CellData ? config.cells : config.cells && config.cells[0];
				const chartConfig = new ColumnComposition({
					renderer: new TextRenderer(cell, Alignment.Horizontal.Center, Alignment.Vertical.Bottom, 100 / arr.length, config.textClasses)
				});
				return chartConfig.renderer.render();
			});
			chartHeader = [new RowPanel('ChartHeaderRow', chartHeaderCells, { paddingLeft: paddingLeft }, Alignment.Horizontal.Left, Alignment.Vertical.Bottom)];
		}
		return new RowPanel(
			name,
			[
				new CellPanel(
					[new RowPanel('TableHeaderRow', tableHeaderCells, {}, Alignment.Horizontal.Left, Alignment.Vertical.Bottom)],
					{ proportion: isForLegend ? this.leftColumnProportionAsLegend : leftColumnProportion },
					Alignment.Horizontal.Left
				),
				new CellPanel(chartHeader, { proportion: isForLegend ? this.rightColumnProportionAsLegend : rightColumnProportion }, Alignment.Horizontal.Left)
			],
			headerConstraints ? headerConstraints : { paddingBottom: 5, marginBottom: 5, borderBottom: 0.25, borderBottomColor: '#000' },
			Alignment.Horizontal.Center,
			Alignment.Vertical.Bottom
		);
	}

	public static getTableAndChartBodyRow(
		name: string,
		tabData: TabData,
		columnCompositions: ColumnCompositions,
		chartType: string,
		paddingBottom: number = 7,
		widthOffset: number = 0,
		chartName: string | ColumnChartName = '',
		highchartsCustomAttributes: any = {},
		leftColumnProportion?: number,
		rightColumnProportion?: number
	): AbstractRenderer<SVGGElement> {
		if (!columnCompositions || !columnCompositions.body) {
			console.error(`Column configurations are not defined, table: ${name} will not render!`);
			return;
		}

		leftColumnProportion = leftColumnProportion ?? this.leftColumnProportion;
		rightColumnProportion = rightColumnProportion ?? this.rightColumnProportion;

		const rows: Array<RowPanel> = [];
		columnCompositions.body.forEach((configs: Array<ColumnComposition>, index: number) => {
			rows.push(
				new RowPanel(
					'bodyRow',
					configs.map((config: ColumnComposition) => config.renderer.render()),
					{
						paddingBottom: index === columnCompositions.body.length - 1 ? 3 : paddingBottom
					},
					Alignment.Horizontal.Left,
					Alignment.Vertical.Middle
				)
			);
		});

		return new RowPanel(name, [
			new CellPanel(rows, { proportion: leftColumnProportion }, Alignment.Horizontal.Left),
			new CellPanel(
				[new HighchartsSVGComponent(chartName, {}, tabData, chartType, highchartsCustomAttributes, widthOffset)],
				{ useFullHeight: true, proportion: rightColumnProportion },
				Alignment.Horizontal.Left
			)
		]);
	}

	public static getExposureTableAndChartBodyRow(
		name: string,
		tabData: TabData,
		columnCompositions: ColumnCompositions,
		chartType: string,
		chartName?: string,
		widthOffset?: number,
		paddingBetweenCategory: number = 3,
		highchartsCustomAttributes: ColumnChartCustomAttributes = {},
		legendTableBoxModelConstraintOverride?: Partial<BoxModelConstraint>,
		isForLegend: boolean = false,
		marginBottom: number = 0,
		contentHeight?: number
	): AbstractRenderer<SVGGElement> {
		if (!columnCompositions || !columnCompositions.body) {
			console.error(`Column configurations are not defined, table: ${name} will not render!`);
			return;
		}

		const rows: Array<RowPanel> = this.getExposureTableRows(columnCompositions, paddingBetweenCategory, isForLegend, legendTableBoxModelConstraintOverride);
		return new RowPanel(name, [
			new CellPanel(rows, { proportion: isForLegend ? this.leftColumnProportionAsLegend : this.leftColumnProportion, marginBottom }, Alignment.Horizontal.Left),
			new CellPanel(
				[
					new HighchartsSVGComponent(
						chartName,
						{ paddingLeft: 5 },
						tabData,
						chartType,
						highchartsCustomAttributes,
						widthOffset
					)
				],
				{ useFullHeight: !contentHeight, height: contentHeight, proportion: isForLegend ? this.rightColumnProportionAsLegend : this.rightColumnProportion },
				Alignment.Horizontal.Left
			)]);
	}

	public static getExposureTableRows(
		columnCompositions: ColumnCompositions, paddingBetweenCategory: number = 3, isForLegend: boolean = false, legendTableBoxModelConstraintOverride?: Partial<BoxModelConstraint>
	): Array<RowPanel> {
		const rows: Array<RowPanel> = [];
		columnCompositions.body.forEach((configs: Array<ColumnComposition>, index: number) => {
			let borderBottom = 0;
			let spaceBottom = 0;
			let paddingBottom = paddingBetweenCategory;

			if (index !== columnCompositions.body.length - 1 && get(configs, '[0].cells') && get(columnCompositions, `body[${index + 1}][0].cells`)) {
				const thisCell: CellData = <CellData>configs[0].cells;
				const nextRowFirstCell: CellData = <CellData>columnCompositions.body[index + 1][0].cells;

				if (thisCell.parent && !nextRowFirstCell.parent) {
					borderBottom = 0.25;
					spaceBottom = 3;
					paddingBottom = 3;
				}
			}

			if (isForLegend && configs[0]) {
				const legendCol = this.buildLegendCol(configs[0]);
				rows.push(
					new RowPanel(
						'bodyRow',
						[legendCol.renderer.render()],
						BoxModelConstraint.merge({
							paddingBottom: index === columnCompositions.body.length - 1 ? 2 : paddingBottom,
							marginBottom: index === columnCompositions.body.length - 1 ? 1 : spaceBottom,
							borderBottom: borderBottom,
							borderBottomColor: '#616161'
						}, legendTableBoxModelConstraintOverride),
						Alignment.Horizontal.Left,
						Alignment.Vertical.Middle
					)
				);
			} else {
				rows.push(
					new RowPanel(
						'bodyRow',
						configs.map((config: ColumnComposition) => config.renderer.render()),
						{
							paddingBottom: index === columnCompositions.body.length - 1 ? 2 : paddingBottom,
							marginBottom: index === columnCompositions.body.length - 1 ? 1 : spaceBottom,
							borderBottom: borderBottom,
							borderBottomColor: '#616161'
						},
						Alignment.Horizontal.Left,
						Alignment.Vertical.Middle
					)
				);
			}
		});
		return rows;
	}

	public static getRiskTableAndChartBodyRow(
		name: string,
		tabData: TabData,
		columnCompositions: ColumnCompositions,
		chartType: ChartType,
		chartName?: string | ColumnChartName,
		highchartsCustomAttributes: ColumnChartCustomAttributes = {},
		legendTableBoxModelConstraintOverride?: Partial<BoxModelConstraint>,
		isForLegend: boolean = false
	): AbstractRenderer<SVGGElement> {
		if (!columnCompositions || !columnCompositions.body) {
			console.error(`Column configurations are not defined, table: ${name} will not render!`);
			return;
		}

		const rows: Array<RowPanel> = [];
		columnCompositions.body.forEach((configs: Array<ColumnComposition>, index: number) => {
			let borderBottom = 0;
			let marginBottom = 0;

			if (index !== columnCompositions.body.length - 1) {
				const nextRowFirstCell: CellData = <CellData>columnCompositions.body[index + 1][0].cells;

				if (index === columnCompositions.body.length - 2 || (nextRowFirstCell.attribute && !nextRowFirstCell.attribute.isGroupMember && nextRowFirstCell.children?.length)) {
					borderBottom = 0.25;
					marginBottom = 3;
				}
			}

			if (isForLegend && configs[0]) {
				const legendCol = this.buildLegendCol(configs[0]);
				rows.push(
					new RowPanel(
						'bodyRow',
						[legendCol.renderer.render()],
						BoxModelConstraint.merge({
							paddingBottom: index === columnCompositions.body.length - 1 ? 2 : 3,
							marginBottom: index === columnCompositions.body.length - 1 ? 1 : marginBottom,
							borderBottom: borderBottom,
							borderBottomColor: '#616161'
						}, legendTableBoxModelConstraintOverride),
						Alignment.Horizontal.Left,
						Alignment.Vertical.Middle
					)
				);
			} else {
				rows.push(
					new RowPanel(
						'bodyRow',
						configs.map((config: ColumnComposition) => config.renderer.render()),
						{
							paddingBottom: index === columnCompositions.body.length - 1 ? 2 : 3,
							marginBottom: index === columnCompositions.body.length - 1 ? 1 : marginBottom,
							borderBottom: borderBottom,
							borderBottomColor: '#616161'
						},
						Alignment.Horizontal.Left,
						Alignment.Vertical.Middle
					)
				);
			}
		});

		return new RowPanel(name, [
			new CellPanel(rows, { proportion: isForLegend ? this.leftColumnProportionAsLegend : this.leftColumnProportion }, Alignment.Horizontal.Left),
			new CellPanel(
				[
					new HighchartsSVGComponent(
						chartName,
						{ paddingLeft: 5 },
						tabData,
						chartType,
						highchartsCustomAttributes
					)
				],
				{ useFullHeight: true, proportion: isForLegend ? this.rightColumnProportionAsLegend : this.rightColumnProportion },
				Alignment.Horizontal.Left
			)
		]);
	}

	public static calculateColumnProportions(numberOfColumns: number, availableWidth: number): number[] {
		const ColumnProportions: number[] = fill(Array(numberOfColumns), NumberUtil.precisionRound(availableWidth / numberOfColumns, 2));
		ColumnProportions[ColumnProportions.length - 1] = NumberUtil.precisionRound(
			ColumnProportions[ColumnProportions.length - 1] + (availableWidth - ColumnProportions.reduce((a, b) => a + b, 0)),
			2
		);
		return ColumnProportions;
	}

	public static getIndexInHeadersY(tableData: TableData, id: string): number {
		const headersY: CellData[] = get(tableData, 'headersY');
		if (!headersY) { return -1; }
		return tableData.headersY.findIndex(header => header.config.id === id);
	}

	public static getDesignationTranslatedValues(modelName, portfolioType, contentSvc: ContentService): string {
		// map by outputDesignationName (modelName)
		if (modelName === 'Alternate') {
			return contentSvc.get('ALTERNATE_PORTFOLIO', 'Alternate Portfolio');
		} else if (modelName === 'Advisor Portfolio') {
			return contentSvc.get('ADVISOR_PORTFOLIO', 'Advisor Portfolio');
		} else if (modelName === 'Global Composite Benchmark') {
			return contentSvc.get('GLOBAL_COMPOSITE_BENCHMARK', 'Global Composite Benchmark');
		} else if (modelName === 'Benchmark') {
			return contentSvc.get('BENCHMARK', 'Benchmark');
			// map by portfolioType (portfolioType)
		} else if (portfolioType === 'MODEL_PORTFOLIO') {
			return contentSvc.get('BLACKROCK_MODEL', 'BlackRock Model');
		} else if (portfolioType === 'THIRD_PARTY') {
			return contentSvc.get('THIRD_PARTY', 'Third Party Model');
		} else if (portfolioType === 'PORTFOLIO') {
			return contentSvc.get('PORTFOLIO', 'Portfolio');
		}
	}

	/***
	 * @description getHeaderRowForGridFormatOfData: generates a grid format with "portfolios" on the left, and data point names on the right.
	 */
	public static getHeaderRowForGridFormatOfData(tabData: TabData, boxModelConstraintOverride?: Partial<BoxModelConstraint>): RowPanel {
		const tableDefinitions = this.getTableDefinitionsForGridFormat(tabData);

		let boxModelConstraint;
		if (boxModelConstraintOverride) {
			boxModelConstraint = boxModelConstraintOverride;
		} else {
			boxModelConstraint = { marginBottom: 10, paddingTop: 6, borderBottom: 0.25, borderBottomColor: '#000' };
		}

		const headerRow: RowPanel = new RowPanel(
			'table Header Row',
			tableDefinitions.headers.map((config: ColumnComposition) => config.renderer.render()),
			boxModelConstraint,
			Alignment.Horizontal.Left,
			Alignment.Vertical.Bottom
		);
		return headerRow;
	}

	/***
	 * @description getRowsForGridFormatOfData: generates a grid format with portfolio names on the left and data values on the right.
	 */
	public static getRowsForGridFormatOfData(tabData: TabData, boxModelConstraintOverride?: Partial<BoxModelConstraint>): Array<RowPanel> {
		const tableDefinitions = this.getTableDefinitionsForGridFormat(tabData);
		const rows: Array<RowPanel> = [];

		tableDefinitions.body.forEach((configs: Array<ColumnComposition>, index: number) => {
			const rowRenderer = configs.map((config: ColumnComposition) => config.renderer.render());

			let boxModelConstraint;
			if (boxModelConstraintOverride) {
				boxModelConstraint = boxModelConstraintOverride;
			} else {
				boxModelConstraint =
					index !== tableDefinitions.body.length - 1 ? { marginBottom: 10, paddingTop: 10, borderBottom: 0.25, borderBottomColor: '#616161' } : { paddingTop: 10 };
			}
			rows.push(new RowPanel('bodyRow', rowRenderer, boxModelConstraint, Alignment.Horizontal.Left, Alignment.Vertical.Middle));
		});

		return rows;
	}

	private static getTableDefinitionsForGridFormat(tabData: TabData): ColumnCompositions {
		const visibleTabData: TabData = TabUtil.getVisibleTabData(tabData);
		const tableDefinitions = new DefaultColumnComposition().getColumnCompositions(
			visibleTabData,
			[20, ...TableUtils.calculateColumnProportions(visibleTabData.tableData[0].headersX.length, 80)],
			true
		);
		return tableDefinitions;
	}

	private static getTableHeaderCells(
		tabData: TabData,
		columnCompositions: ColumnCompositions,
		isForLegend: boolean = false,
		shouldUseWeightAsFirstCell: boolean = true
	): AbstractRenderer<SVGElement>[] {
		let tableHeaderCells;
		if (isForLegend) {
			tableHeaderCells = [columnCompositions.headers[0].renderer.render()];
			if (tableHeaderCells.length === 1) {
				tableHeaderCells[0] = new ColumnComposition({
					cells: get(columnCompositions, 'headers[0].cells'),
					widthProportion: 100,
					textClasses: get(columnCompositions, 'headers[0].textClasses'),
					horizontalAlignment: get(columnCompositions, 'headers[0].horizontalAlignment'),
					verticalAlignment: get(columnCompositions, 'headers[0].verticalAlignment'),
					boxModelConstraint: get(columnCompositions, 'headers[0].boxModelConstraint')
				}).renderer.render();
			}
		} else {
			const numOfPortfolios: number = tabData.tableData[0].headersX.length;
			tableHeaderCells = columnCompositions.headers.map((config: ColumnComposition) => config.renderer.render());
			if (numOfPortfolios === 1 && tableHeaderCells[1] && shouldUseWeightAsFirstCell) {
				tableHeaderCells[1] = new ColumnComposition({
					cells: [new CellData(null, 'Weight')],
					widthProportion: get(columnCompositions, 'headers[1].widthProportion'),
					textClasses: get(columnCompositions, 'headers[1].textClasses'),
					horizontalAlignment: get(columnCompositions, 'headers[1].horizontalAlignment'),
					verticalAlignment: get(columnCompositions, 'headers[1].verticalAlignment'),
					boxModelConstraint: get(columnCompositions, 'headers[1].boxModelConstraint')
				}).renderer.render();
			}
		}
		return tableHeaderCells;
	}

	private static buildLegendCol(categoryCol): ColumnComposition {
		return new ColumnComposition({
			cells: categoryCol.cells,
			horizontalAlignment: categoryCol.horizontalAlignment,
			verticalAlignment: categoryCol.verticalAlignment,
			widthProportion: 100,
			cellClasses: categoryCol.cellClasses,
			textClasses: categoryCol.textClasses,
			renderer: categoryCol.renderer,
			boxModelConstraint: categoryCol.boxModelConstraint,
			cellBoxModelConstraint: categoryCol.cellBoxModelConstraint
		});
	}
}
